// Η προκαταβολή μέσα στην οθόνη «Κρατήσεις» του ταμείου (μόνο με ανοιχτό τον διακόπτη
// counter_deposits_enabled): ένδειξη στη γραμμή της κράτησης και ενότητα στις λεπτομέρειες με
// «Προκαταβολή» / «Επιστροφή», το 8.4, οφειλόμενες επιστροφές και ό,τι κρατήθηκε.

import React, { useCallback, useEffect, useState } from 'react';
import { Banknote, RotateCcw } from 'lucide-react';
import { Btn, Note } from '../dineIn/floorEditor/ui';
import { depositsApi } from './api';
import DepositModal, { type DepositReservation } from './DepositModal';
import { isOpen, money, reservationTakesDeposit, sortDeposits, toCents } from './model';
import RefundModal from './RefundModal';
import { ReceiptLine, useDT } from './ui';
import type { DepositView, DepositsConfig, ReservationDepositSummary } from './types';

type ResLike = DepositReservation & {
  status: string;
  deposit_status?: string | null;
  deposit?: ReservationDepositSummary | null;
};

/** Οι προκαταβολές ταμείου των κρατήσεων (ένα αίτημα ανά φόρτωμα της ημέρας), ανά κράτηση. */
export function useCounterDepositsByReservation(storeId: number, enabled: boolean, refreshKey: unknown) {
  const [map, setMap] = useState<Map<number, DepositView[]>>(() => new Map());
  useEffect(() => {
    if (!enabled) { setMap(new Map()); return undefined; }
    let alive = true;
    depositsApi.list(storeId, { status: 'all', limit: 200 })
      .then(list => {
        if (!alive) return;
        const m = new Map<number, DepositView[]>();
        list.forEach(d => {
          if (d.reservation_id == null || d.status === 'failed') return;
          m.set(d.reservation_id, [...(m.get(d.reservation_id) || []), d]);
        });
        setMap(m);
      })
      .catch(() => { /* χωρίς λίστα: μένει η σύνοψη της κράτησης */ });
    return () => { alive = false; };
  }, [storeId, enabled, refreshKey]);
  return map;
}

type Tone = 'info' | 'ok' | 'warn' | 'danger' | 'muted';
const TONE: Record<Tone, string> = {
  info: 'bg-blue-500/12 text-blue-700 dark:text-blue-300',
  ok: 'bg-emerald-500/12 text-emerald-700 dark:text-emerald-300',
  warn: 'bg-amber-500/12 text-amber-700 dark:text-amber-300',
  danger: 'bg-red-500/12 text-red-700 dark:text-red-300',
  muted: 'bg-surface-3 text-t3',
};

/** Τι λέει η ένδειξη μιας κράτησης: πρώτα η προκαταβολή ταμείου (με το 8.4), αλλιώς η σύνοψη. */
function chipOf(dt: ReturnType<typeof useDT>, r: ResLike, views?: DepositView[]): { tone: Tone; text: string; title?: string } | null {
  const v = views && views.length ? sortDeposits(views)[0] : null;
  const s = r.deposit || null;
  const kept = v ? toCents(v.kept) : toCents(s?.kept);
  const settlement = v ? v.settlement : s?.settlement;
  const settlementError = v ? v.settlement_error : s?.settlement_error;
  const refundDue = v ? v.refund_due && isOpen(v) : s?.refund_due === true;

  if (refundDue) return { tone: 'danger', text: dt('deposits:chip.refundDue', { amount: money(v ? v.available : (toCents(s?.paid) - toCents(s?.refunded)) / 100) }) };
  if (kept > 0) {
    return settlement === 'settled'
      ? { tone: 'muted', text: `${dt('deposits:chip.kept', { amount: money(kept / 100) })} · ${dt('deposits:chip.settled')}` }
      : { tone: 'warn', text: `${dt('deposits:chip.kept', { amount: money(kept / 100) })} · ${dt('deposits:chip.settlementPending')}`, title: settlementError || undefined };
  }
  if (v) {
    if (v.status === 'pending') return { tone: 'warn', text: dt('deposits:chip.charging', { amount: money(v.amount) }) };
    if (v.status === 'refunded') return { tone: 'muted', text: dt('deposits:chip.refunded', { amount: money(v.refunded) }) };
    if (v.status === 'applied') return { tone: 'muted', text: dt('deposits:chip.applied', { amount: money(v.amount) }) };
    const base = `${dt('deposits:chip.counter', { amount: money(v.amount) })} · ${dt(`deposits:method.${v.method}`)}`;
    if (v.method === 'cash') return { tone: 'ok', text: base };
    if (v.receipt && v.receipt.mark) return { tone: 'ok', text: `${base} · 8.4 ${[v.receipt.series, v.receipt.number].filter(x => x != null && x !== '').join('-')}` };
    return { tone: 'warn', text: `${base} · ${dt('deposits:receipt.pending', { doc: '8.4' })}`, title: v.receipt_error || undefined };
  }
  const status = s?.status || r.deposit_status || null;
  const paid = toCents(s?.paid);
  const due = toCents(s?.due ?? r.deposit_due);
  switch (status) {
    case 'awaiting': return { tone: 'warn', text: dt('deposits:chip.awaiting', { amount: money(due / 100) }) };
    case 'paid':
      return s?.channel === 'counter' && s.method
        ? { tone: 'ok', text: `${dt('deposits:chip.counter', { amount: money((paid || due) / 100) })} · ${dt(`deposits:method.${s.method}`)}` }
        : { tone: 'info', text: dt('deposits:chip.online', { amount: money((paid || due) / 100) }) };
    case 'applied': return { tone: 'muted', text: dt('deposits:chip.applied', { amount: money((paid || due) / 100) }) };
    case 'refunded': return { tone: 'muted', text: dt('deposits:chip.refunded', { amount: money((toCents(s?.refunded) || paid || due) / 100) }) };
    case 'forfeited': return { tone: 'warn', text: dt('deposits:chip.kept', { amount: money((paid || due) / 100) }) };
    default: return null;
  }
}

/** Η ένδειξη στη γραμμή της κράτησης. */
export function DepositChip({ r, views }: { r: ResLike; views?: DepositView[] }) {
  const dt = useDT();
  const c = chipOf(dt, r, views);
  if (!c) return null;
  return (
    <span data-testid="deposit-chip" title={c.title}
      className={`inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[11.5px] font-semibold ${TONE[c.tone]}`}>
      <Banknote className="h-3.5 w-3.5 shrink-0" />{c.text}
    </span>
  );
}

/** Μία προκαταβολή ταμείου: ποσό, τρόπος, 8.4, επιστροφές, ό,τι κρατήθηκε — και «Επιστροφή». */
export function DepositCard({ d, canRefund, onRefund }: { d: DepositView; canRefund: boolean; onRefund: () => void }) {
  const dt = useDT();
  const kept = toCents(d.kept);
  return (
    <div data-testid={`deposit-card-${d.id}`} className={`grid gap-1.5 rounded-xl border px-3.5 py-3 ${d.refund_due && isOpen(d) ? 'border-red-500/60 bg-red-500/5' : 'border-line bg-surface-1'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-[16px] font-bold tabular-nums text-t1">{money(d.amount)} · {dt(`deposits:method.${d.method}`)}</span>
        <span className="text-[12px] font-semibold text-t3">{dt(`deposits:status.${d.status}`)}</span>
      </div>
      <ReceiptLine doc="8.4" receipt={d.receipt} pending={d.receipt_pending} error={d.receipt_error} cash={d.method === 'cash'} />
      {toCents(d.refunded) > 0 && <span className="text-[12.5px] text-t2">{dt('deposits:detail.refunded', { amount: money(d.refunded) })}</span>}
      {toCents(d.used) > 0 && <span className="text-[12.5px] text-t2">{dt('deposits:detail.used', { amount: money(d.used) })}</span>}
      {isOpen(d) && toCents(d.available) !== toCents(d.amount) && (
        <span className="text-[12.5px] text-t2">{dt('deposits:detail.available', { amount: money(d.available) })}</span>
      )}
      {d.refund_due && isOpen(d) && (
        <span data-testid="deposit-refund-due" className="text-[13px] font-semibold text-red-700 dark:text-red-300">
          {dt('deposits:detail.refundDue', { amount: money(d.available) })}
        </span>
      )}
      {kept > 0 && (
        <span data-testid="deposit-kept" className={`text-[12.5px] font-semibold ${d.settlement === 'settled' ? 'text-t2' : 'text-amber-700 dark:text-amber-300'}`}
          title={d.settlement_error || undefined}>
          {dt('deposits:chip.kept', { amount: money(d.kept) })}
          {d.kept_kind && ` (${dt(`deposits:detail.kept.${d.kept_kind}`)})`}
          {' · '}{d.settlement === 'settled' ? dt('deposits:chip.settled') : dt('deposits:chip.settlementPending')}
          {d.settlement !== 'settled' && d.settlement_error && <span className="block font-normal text-t3">{d.settlement_error}</span>}
        </span>
      )}
      {isOpen(d) && canRefund && (
        <div className="pt-1">
          <Btn size="sm" variant={d.refund_due ? 'danger' : 'secondary'} icon={<RotateCcw className="h-4 w-4" />} onClick={onRefund}
            data-testid={`deposit-refund-${d.id}`}>
            {dt('deposits:refund')}
          </Btn>
        </div>
      )}
    </div>
  );
}

/** Ενότητα «Προκαταβολή» στις λεπτομέρειες της κράτησης. */
export function ReservationDepositSection({ storeId, r, config, canCollect, refreshKey, onChanged }: {
  storeId: number; r: ResLike; config: DepositsConfig; canCollect: boolean; refreshKey?: unknown; onChanged: () => void;
}) {
  const dt = useDT();
  const [list, setList] = useState<DepositView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<{ kind: 'new' } | { kind: 'refund'; d: DepositView } | null>(null);

  const load = useCallback(async () => {
    try {
      const all = await depositsApi.list(storeId, { reservationId: r.id, status: 'all' });
      setList(sortDeposits(all.filter(d => d.status !== 'failed')));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [storeId, r.id]);
  useEffect(() => { setList(null); load(); }, [load, refreshKey]);

  const changed = () => { load(); onChanged(); };
  const takes = reservationTakesDeposit(r) && !(list || []).some(d => d.status === 'pending' || isOpen(d));
  const online = r.deposit && r.deposit.channel && r.deposit.channel !== 'counter' ? r.deposit : null;

  return (
    <section data-testid="reservation-deposit" className="grid gap-2 rounded-xl border border-line bg-surface-2/60 p-3">
      <h4 className="flex items-center gap-1.5 text-[13px] font-semibold uppercase tracking-wide text-t3">
        <Banknote className="h-4 w-4" />{dt('deposits:detail.title')}
      </h4>
      {error && <Note tone="warn">{error}</Note>}
      {list == null && !error && <div className="h-12 animate-pulse rounded-lg bg-surface-2" />}
      {list && list.map(d => (
        <DepositCard key={d.id} d={d} canRefund={canCollect} onRefund={() => setModal({ kind: 'refund', d })} />
      ))}
      {online && list && list.length === 0 && (
        <div className="grid gap-1">
          <DepositChip r={r} />
          <span className="text-[12px] text-t3">{dt('deposits:detail.onlineHint')}</span>
        </div>
      )}
      {list && list.length === 0 && !online && !takes && r.deposit_status && <DepositChip r={r} />}
      {takes && canCollect && (
        <Btn variant="primary" className="h-11 justify-center text-[15px]" icon={<Banknote className="h-4 w-4" />}
          onClick={() => setModal({ kind: 'new' })} data-testid="deposit-take">
          {dt('deposits:take')}
        </Btn>
      )}
      {takes && canCollect && !config.ready && <span className="text-[12px] text-t3">{dt('deposits:notReady')}</span>}
      {list && list.length === 0 && !online && !takes && !r.deposit_status && <span className="text-[13px] text-t3">{dt('deposits:detail.none')}</span>}

      {modal?.kind === 'new' && (
        <DepositModal storeId={storeId} config={config} owner={{ kind: 'reservation', reservation: r }}
          onClose={() => setModal(null)} onDone={changed} />
      )}
      {modal?.kind === 'refund' && (
        <RefundModal storeId={storeId} deposit={modal.d} onClose={() => setModal(null)} onDone={changed} />
      )}
    </section>
  );
}
